import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NoteMarkdown } from "@baluu/ui/components/checkout/note-markdown";
import { fetchSchoolCheckoutConfig } from "~/widgets/lib/school-checkout-config";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool, schoolIdOf } from "../school";
import { formatMoney } from "../money";
import {
  baselineOf,
  instalmentOf,
  isCancelled,
  isFullyPaid,
  mergedPlan,
  orderTitle,
  parseBaseline,
  payableOption,
  paymentLanded,
  progressOf,
  remainingTitle,
  startPaymentBody,
  type PaymentBaseline,
  type PrivateBooking,
  type PrivateService,
  type SecurePaymentPage,
  type StartPaymentResponse,
} from "./secure/securePayment";
import { RETURN_CANCELLED, RETURN_FAILED, goToHostedCheckout } from "./secure/stripeReturn";
import { CONFIRM_POLL_MS, pageUrl, useStripeReturn } from "./secure/useStripeReturn";
import { StripePaymentDialog } from "./secure/StripePaymentDialog";
import {
  PaymentPendingNotice,
  PaymentPlanOption,
  PaymentProgress,
  PrivateBookingBox,
  PrivateServiceBox,
  StickyPayBar,
} from "./secure/SecurePaymentParts";

// /secure/payment/<order-id>[/<instalment-id>]: a payment link a school
// sends for a private event or a service. A port of v1's secure payment
// page: the order, its plan (pay any instalment, the next one, or what's
// left), the invoice, and paying by card on the school's Stripe account.
// Stripe brings the customer back to this page on this host; the page then
// waits for the order to show the payment before offering to pay again.

type Intent = {
  clientSecret: string;
  stripeAccount?: string;
  publishableKey: string;
  amount: number;
  baseline: PaymentBaseline;
};

export function SecurePaymentView({ ctx, orderId, instalment }: { ctx: ViewContext; orderId: string; instalment: string }) {
  // A return from Stripe (or v1's `success` flag), and the "paid, not
  // showing yet" mark that keeps Pay hidden for this order in this tab.
  const ret = useStripeReturn<PaymentBaseline>(`secure-payment:${orderId}`, parseBaseline);
  const { returned, paid } = ret;
  const [starting, setStarting] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [intent, setIntent] = useState<Intent | null>(null);

  const slug = ctx.businessSlug.trim();
  const url = `${ctx.api}/widget/pages/secure-payment/${encodeURIComponent(slug)}/${encodeURIComponent(orderId)}`;
  const page = useQuery({
    queryKey: ["booking", "secure-payment", ctx.api, slug, orderId],
    queryFn: () => bookingJson<SecurePaymentPage>(url),
    enabled: !!slug,
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
    // After a payment, re-read until the order shows it (the webhook that
    // records it runs a moment later), for a minute; then by hand.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!paid || !ret.polling || !data?.order) return false;
      return paymentLanded(data.order, paid.baseline) ? false : CONFIRM_POLL_MS;
    },
  });

  // The publishable key, ahead of Pay: no payment starts without it.
  const keySchool = page.data?.order?.schoolID || ctx.businessId;
  const stripeKey = useQuery({
    queryKey: ["booking", "checkout-config", ctx.api, keySchool],
    queryFn: () => fetchSchoolCheckoutConfig<{ stripePublicKey?: string }>(keySchool, ctx.api),
    enabled: !!keySchool,
    staleTime: 5 * 60 * 1000,
    select: (config) => config.stripePublicKey || null,
  });

  const landedNow = !!paid && !!page.data?.order && paymentLanded(page.data.order, paid.baseline);
  useEffect(() => {
    if (landedNow) ret.settle();
  }, [landedNow, ret]);

  if (!slug) return <RecordNotFound what="payment" />;
  if (page.isLoading) return <BookingLoading rows={3} />;
  if (page.isError) {
    const status = page.error instanceof BookingApiError ? page.error.status : 0;
    return status === 404 ? (
      <RecordNotFound what="payment" />
    ) : (
      <BookingProblem
        title="We couldn’t load this payment"
        message={messageOf(page.error, "Please try again in a moment.")}
        onRetry={() => page.refetch()}
      />
    );
  }
  const data = page.data;
  if (!data?.order || isOtherSchool(ctx.businessId, data.order.schoolID, schoolIdOf(data.school))) return <RecordNotFound what="payment" />;

  const { order, relatedItem, school } = data;
  const named = instalmentOf(order, instalment);
  // An instalment that isn't in the plan: v1's 404.
  if (named === "missing") return <RecordNotFound what="payment" />;

  const fullyPaid = isFullyPaid(order);
  const cancelled = isCancelled(order);
  // Just paid, and the order doesn't show it yet: no Pay at all until it
  // does (or the mark expires), whatever the time.
  const confirming = !!paid && !landedNow && !fullyPaid;
  const plan = mergedPlan(order);
  const payable = confirming ? null : payableOption(order, named);
  const publishableKey = stripeKey.data ?? null;
  const progress = progressOf(order);
  const remaining = remainingTitle(order);
  const currency = order.currencyUsed || "GBP";
  const booking = order.orderType === "booking" && relatedItem && "privateCourse" in relatedItem ? (relatedItem as PrivateBooking) : null;
  const service = order.orderType === "service" && relatedItem && "name" in relatedItem ? (relatedItem as PrivateService) : null;
  const open = !cancelled && !fullyPaid && !confirming;
  const busy = starting !== null || intent !== null || !publishableKey;

  async function startPayment(instalmentId?: string) {
    if (busy || confirming) return;
    if (!publishableKey) {
      setStartError("Card payments aren’t available right now. Please try again in a moment.");
      return;
    }
    setStarting(instalmentId ?? "remaining");
    setStartError(null);
    try {
      const result = await sendJson<StartPaymentResponse>(
        `${ctx.api}/widget/secure-payment/${encodeURIComponent(order._id)}/start-payment`,
        "POST",
        startPaymentBody(window.location, pageUrl(ctx), instalmentId),
      );
      const baseline = baselineOf(order, instalmentId);
      if (result.usedStripeHostedPage && result.checkoutURL) {
        ret.rememberStart(baseline);
        goToHostedCheckout(result.checkoutURL);
        return;
      }
      if (!result.paymentIntentClientSecret) throw new Error("no client secret");
      // A card that has to leave the page comes back with this.
      ret.rememberStart(baseline);
      setIntent({
        clientSecret: result.paymentIntentClientSecret,
        stripeAccount: result.paymentMethodType === "direct" ? result.schoolAccountID : undefined,
        publishableKey,
        amount: result.finalPrice,
        baseline,
      });
    } catch (e) {
      if (e instanceof BookingApiError && e.status === 409) {
        // Paid since the page was opened (by card or recorded offline).
        setStartError("This has already been paid.");
        void page.refetch();
      } else if (e instanceof BookingApiError && e.status === 404) {
        setStartError("This payment option isn’t available any more. Please refresh the page.");
        void page.refetch();
      } else {
        setStartError(messageOf(e, "We couldn’t start your payment. Please try again in a moment."));
      }
    } finally {
      setStarting(null);
    }
  }

  function onPaid() {
    const baseline = intent?.baseline ?? null;
    setIntent(null);
    ret.markPaid(baseline);
    void page.refetch();
  }

  function closeDialog() {
    ret.forgetStart();
    setIntent(null);
  }

  const option = (args: { key: string; name: string; amount: number; isPaid?: boolean; dueDate?: string; instalmentId?: string }) => (
    <PaymentPlanOption
      key={args.key}
      name={args.name}
      amount={args.amount}
      currency={currency}
      isPaid={args.isPaid}
      dueDate={args.dueDate}
      timezone={school.timezone}
      onPay={() => void startPayment(args.instalmentId)}
      disabled={busy}
      starting={starting === (args.instalmentId ?? "remaining")}
    />
  );

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="space-y-1">
        <h1 className="text-[1.75rem] font-semibold sm:text-[2.5rem]">{orderTitle(order, relatedItem)}</h1>
        <p className="text-sm text-muted-foreground">Order Reference: {order._id}</p>
      </header>

      {(fullyPaid || (paid && !confirming)) && <Notice tone="success">Thank you, your payment has been received</Notice>}
      {confirming && (
        <PaymentPendingNotice
          polling={ret.polling}
          schoolName={school.name || "the school"}
          onCheck={() => void page.refetch()}
          checking={page.isFetching}
        />
      )}
      {returned.outcome === "failed" && !paid && <Notice tone="error">{RETURN_FAILED}</Notice>}
      {returned.outcome === "cancelled" && <Notice tone="info">{RETURN_CANCELLED}</Notice>}
      {startError && <Notice tone="error">{startError}</Notice>}
      {open && (stripeKey.isError || (stripeKey.isSuccess && !publishableKey)) && (
        <Notice tone="error">
          Card payments aren’t available right now.{" "}
          <button type="button" className="underline" onClick={() => void stripeKey.refetch()}>
            Try again
          </button>
        </Notice>
      )}

      <div className="flex flex-col gap-5 sm:flex-row">
        <div className="min-w-0 space-y-4 rounded-lg border bg-card p-5 sm:flex-1 sm:p-8">
          {progress && (
            <PaymentProgress
              paid={progress.paid}
              total={progress.total}
              percent={progress.percent}
              currency={currency}
              paidInstalments={progress.paidInstalments}
              totalInstalments={progress.totalInstalments}
            />
          )}
          {fullyPaid && <p className="text-sm">Paid</p>}
          {cancelled && <p className="text-sm">Cancelled</p>}
          <p className="text-sm">
            <a className="underline" target="_blank" rel="noreferrer" href={`https://app.baluu.io/public/invoices/${order._id}`}>
              Download your invoice
            </a>
          </p>
          {open && order.orderType === "booking" && (
            <p className="text-sm">
              A private event has been created for you by {school.name}. Please check the details and then click a link to pay securely.
            </p>
          )}
          {open && service?.description && <NoteMarkdown className="space-y-2 text-sm leading-relaxed">{service.description}</NoteMarkdown>}
          {booking?.privateCourse.additionalDetails && (
            <p className="text-sm">
              <strong>Additional Details</strong>
              <br />
              {booking.privateCourse.additionalDetails}
            </p>
          )}

          {open && (
            <div className="space-y-2.5">
              {named ? (
                option({ key: named.id, name: named.name, amount: named.amount, isPaid: named.isPaid, dueDate: named.dueDate, instalmentId: named.id })
              ) : (
                <>
                  {plan.length > 0 && (
                    <>
                      <p className="text-sm">Pay an instalment by choosing an option below.</p>
                      {plan.map((o) =>
                        option({ key: o.id, name: o.name, amount: o.amount, isPaid: o.isPaid, dueDate: o.dueDate, instalmentId: o.id }),
                      )}
                      <hr className="my-5 border-border" />
                      <p className="text-sm">Alternatively pay the {remaining.toLowerCase()} now.</p>
                    </>
                  )}
                  {option({ key: "remaining", name: remaining, amount: order.amountRemaining, dueDate: order.paymentDueDate })}
                </>
              )}
            </div>
          )}

          {order.orderType === "booking" && school.customTerms?.privateEvents != null && (
            <p className="text-sm text-muted-foreground">
              <a className="underline" target="_blank" rel="noreferrer" href={ctx.links.site("/terms#private-events")}>
                Review the terms and conditions
              </a>{" "}
              for this event.
            </p>
          )}
        </div>

        <div className="sm:w-[220px] sm:shrink-0 md:w-[320px]">
          {booking && <PrivateBookingBox order={order} booking={booking} school={school} />}
          {service && <PrivateServiceBox order={order} service={service} school={school} />}
        </div>
      </div>

      {payable && (
        <StickyPayBar
          name={payable.name}
          amount={payable.amount}
          currency={currency}
          onPay={() => void startPayment(payable.instalmentId)}
          disabled={busy}
        />
      )}

      {intent && (
        <StripePaymentDialog
          publishableKey={intent.publishableKey}
          clientSecret={intent.clientSecret}
          stripeAccount={intent.stripeAccount}
          amountLabel={formatMoney(intent.amount, currency)}
          returnUrl={pageUrl(ctx)}
          primaryColor={ctx.primaryColor}
          onPaid={onPaid}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
